/**
 * Sistema Universal de Logros y Trofeos (Biofeedback Gaming)
 * 
 * Almacenamiento persistente en localStorage: 'manabi_game_achievements'
 */

export const TROPHY_TIERS = {
  bronze:   { name: 'Bronce',  color: '#cd7f32', bg: 'rgba(205, 127, 50, 0.15)' },
  silver:   { name: 'Plata',   color: '#c0c0c0', bg: 'rgba(192, 192, 192, 0.15)' },
  gold:     { name: 'Oro',     color: '#ffd700', bg: 'rgba(255, 215, 0, 0.15)' },
  platinum: { name: 'Platino', color: '#64d2ff', bg: 'rgba(100, 210, 255, 0.18)' },
}

export const ACHIEVEMENTS_LIST = [
  // ── Nave Zen (Juego 1) ──
  {
    id: 'first_flight',
    title: 'Primer Despegue',
    subtitle: 'Completá tu primera partida en Nave Zen',
    tier: 'bronze',
    game: 'zen_runner',
    points: 50,
  },
  {
    id: 'zen_pilot_100',
    title: 'Piloto Calmo',
    subtitle: 'Alcanzá 100 puntos en un solo vuelo',
    tier: 'bronze',
    game: 'zen_runner',
    points: 100,
  },
  {
    id: 'zen_pilot_500',
    title: 'Navegante Zen',
    subtitle: 'Alcanzá 500 puntos en un solo vuelo',
    tier: 'silver',
    game: 'zen_runner',
    points: 250,
  },
  {
    id: 'zen_pilot_1000',
    title: 'Maestro del Pulso',
    subtitle: 'Superá los 1.000 puntos en una sola partida',
    tier: 'gold',
    game: 'zen_runner',
    points: 500,
  },
  {
    id: 'calm_streak_30',
    title: 'Corazón de Hierro',
    subtitle: 'Mantené tus pulsaciones por debajo de 75 BPM durante 30 segundos de vuelo',
    tier: 'silver',
    game: 'zen_runner',
    points: 300,
  },
  {
    id: 'calm_streak_60',
    title: 'Monje Bioeléctrico',
    subtitle: 'Mantené tus pulsaciones en calma continua durante 60 segundos con multiplicador x3 activo',
    tier: 'gold',
    game: 'zen_runner',
    points: 600,
  },
  {
    id: 'orb_collector_50',
    title: 'Recolector de Energía',
    subtitle: 'Atrapá 50 orbes de calma en un solo vuelo',
    tier: 'bronze',
    game: 'zen_runner',
    points: 150,
  },
  {
    id: 'flawless_flight',
    title: 'Vuelo Impecable',
    subtitle: 'Volá durante 2 minutos sin recibir daño por turbulencia',
    tier: 'platinum',
    game: 'zen_runner',
    points: 1000,
  },

  // ── Polar Boxing: Sombra y Reflejos ──
  {
    id: 'boxing_first_fight',
    title: 'Primer Asalto',
    subtitle: 'Completá tu primer combate de sombra con el Polar H10',
    tier: 'bronze',
    game: 'boxing',
    points: 75,
  },
  {
    id: 'boxing_slip_master',
    title: 'Cintura de Oro',
    subtitle: 'Esquivá 10 golpes seguidos con movimientos de torso',
    tier: 'silver',
    game: 'boxing',
    points: 300,
  },
  {
    id: 'boxing_ko_champion',
    title: 'Pegada de Demolición',
    subtitle: 'Noqueá al rival con un contragolpe crítico en modo Sangre Fría',
    tier: 'gold',
    game: 'boxing',
    points: 500,
  },
  {
    id: 'boxing_cold_blood',
    title: 'Sangre de Hielo',
    subtitle: 'Peleá durante 60 segundos manteniendo tu pulso por debajo de 115 BPM',
    tier: 'platinum',
    game: 'boxing',
    points: 800,
  },

  // ── Logros Universales de Biofeedback ──
  {
    id: 'first_polar_connection',
    title: 'Sincronía Biológica',
    subtitle: 'Jugá una partida con tu sensor Polar H10 conectado en vivo',
    tier: 'bronze',
    game: 'general',
    points: 100,
  },
  {
    id: 'deep_relaxation',
    title: 'Oasis Parasimpático',
    subtitle: 'Lográ un HRV (RMSSD) superior a 65 ms mientras jugás',
    tier: 'silver',
    game: 'general',
    points: 350,
  },
  {
    id: 'daily_player',
    title: 'Constancia Diaria',
    subtitle: 'Entrená tu control cardiovascular durante 3 días consecutivos',
    tier: 'gold',
    game: 'general',
    points: 500,
  },
  {
    id: 'grand_master',
    title: 'Gran Maestro de Manabi',
    subtitle: 'Desbloqueá al menos 8 trofeos de la plataforma',
    tier: 'platinum',
    game: 'general',
    points: 1500,
  }
]

const STORAGE_KEY = 'manabi_game_achievements'

// Obtiene los logros desbloqueados guardados
export function getUnlockedAchievements() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

// Desbloquea un logro y notifica a los suscriptores
const listeners = new Set()

export function subscribeAchievementUnlocked(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function unlockAchievement(achievementId) {
  const ach = ACHIEVEMENTS_LIST.find(a => a.id === achievementId)
  if (!ach) return false

  const current = getUnlockedAchievements()
  if (current[achievementId]) return false // ya desbloqueado

  const updated = {
    ...current,
    [achievementId]: {
      unlockedAt: Date.now(),
      title: ach.title,
      tier: ach.tier,
      points: ach.points,
    }
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch (_) {}

  // Notificar a oyentes
  listeners.forEach(fn => {
    try { fn(ach) } catch (_) {}
  })

  // Chequear si se desbloqueó el trofeo maestro
  const count = Object.keys(updated).length
  if (count >= 8 && !updated['grand_master'] && achievementId !== 'grand_master') {
    unlockAchievement('grand_master')
  }

  return true
}

// Evalúa las estadísticas de fin de partida o en vivo para desbloquear trofeos
export function evaluateGameStats({ score, durationSec, calmSec, orbs, hits, isConnected, maxRmssd }) {
  const unlockedNow = []

  const check = (id, cond) => {
    if (cond) {
      const ok = unlockAchievement(id)
      if (ok) {
        const ach = ACHIEVEMENTS_LIST.find(a => a.id === id)
        if (ach) unlockedNow.push(ach)
      }
    }
  }

  check('first_flight', true)
  if (score >= 100) check('zen_pilot_100', true)
  if (score >= 500) check('zen_pilot_500', true)
  if (score >= 1000) check('zen_pilot_1000', true)
  if (calmSec >= 30) check('calm_streak_30', true)
  if (calmSec >= 60) check('calm_streak_60', true)
  if (orbs >= 50) check('orb_collector_50', true)
  if (durationSec >= 120 && hits === 0) check('flawless_flight', true)
  if (isConnected) check('first_polar_connection', true)
  if (maxRmssd >= 65) check('deep_relaxation', true)

  return unlockedNow
}
